import { createDrawer, DrawerController } from './drawer';
import { addToCart } from './cart-functions';
import { openCartDrawer } from './cart-drawer';

/**
 * ATC promo chooser drawer (complex-promos.md §4).
 *
 * When a product the user is adding qualifies for 2+ promos, the `add` endpoint declines to add and
 * returns the rendered chooser body instead; addToCart() relays that as a `promoChooser:open` event.
 * This module injects that body into the shared drawer, opens it, and — once the user picks a promo
 * and clicks "Proceed to cart" — re-adds the product pinned to the chosen promo + earn layer.
 */
export function initPromoChooserDrawer() {
  const el = document.getElementById('js-promo-chooser-drawer');
  if (!el) {
    return;
  }
  const drawer: DrawerController | null = createDrawer(el);
  if (!drawer) {
    return;
  }

  // The server (via addToCart) asks us to let the user choose a promo for the product they're adding.
  window.addEventListener('promoChooser:open', (event) => {
    const html = (event as CustomEvent).detail?.html;
    if (!html) {
      return;
    }
    const content = el.querySelector('.js-promo-chooser-content');
    if (!content) {
      return;
    }
    content.innerHTML = html;
    drawer.open();
  });

  // "Proceed to cart" — add the product pinned to the chosen promo, then swap to the cart drawer.
  el.addEventListener('click', async (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLButtonElement>('.js-promo-chooser-proceed');
    if (!btn) {
      return;
    }

    const form = el.querySelector<HTMLElement>('.js-promo-chooser-form');
    const selected = el.querySelector<HTMLInputElement>('.js-promo-choice:checked');
    if (!form || !selected) {
      return;
    }

    const productId = form.dataset.productId;
    const quantity = parseInt(form.dataset.quantity || '1', 10) || 1;
    const quantityBreakStr = form.dataset.quantityBreak;
    const quantityBreak = quantityBreakStr ? parseInt(quantityBreakStr, 10) : undefined;
    const promoId = parseInt(selected.dataset.promoId || '', 10);
    const layerId = parseInt(selected.dataset.layerId || '', 10);
    if (!productId || Number.isNaN(promoId) || Number.isNaN(layerId)) {
      return;
    }

    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    const message = quantity > 1 ? `${quantity} items added to cart` : '1 item added to cart';

    btn.disabled = true;
    btn.classList.add('processing');
    try {
      // Pinned add: carries promoId/layerId, so the server skips the chooser check and just adds it
      // to the chosen promo. openCart=false — we close this drawer first, then open the cart below.
      await addToCart({ id: productId, quantity, quantityBreak, promoId, layerId }, csrf, false, message);
      drawer.close();
      openCartDrawer();
    } catch (err) {
      console.error('[PromoChooser] failed to add the chosen promo item', err);
    } finally {
      btn.disabled = false;
      btn.classList.remove('processing');
    }
  });
}
